'use client'

import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { cn } from '@/lib/utils'
import {
  AdditionalAction,
  DataTable,
  type DataTableColumnDef,
  ToolbarAction
} from '@frontend/components/enhanced/data-table'
import { handleName, type RawTeamData } from '@frontend/core'
import { IMAGE_PRESETS } from '@frontend/core/lib/modules/images'
import { useMemo, useState } from 'react'
import { StaticAssetImage } from '../../modules/images/components'
// Create team form //
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/form'
import { Input } from '@/components/input'
import {
  useLinkTeamWithOrgMutation,
  useUnlinkTeamFromOrgMutation
} from '@frontend/core/lib/domains/organisations'
import {
  getSport,
  getSportName,
  SPORTS
} from '@frontend/core/lib/domains/sports'
import {
  useCreateTeamMutation,
  useUpdateTeamMutation
} from '@frontend/core/lib/domains/teams'
import { addNotification } from '@frontend/core/lib/modules/notifications'
import { toast } from '@frontend/ui'
import { Link2, Unlink } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { useAppDispatch } from '../../store/hooks'
import { requiredResolver } from '../../utils/form-resolver'
import { SuperAdminOnly } from '../components'
import { useIsSuperAdmin } from '../hooks'
import { useSelectedOrganisation } from '../organisations'
import { LinkTeamModal } from './components/link-team-modal'
import { UnlinkTeamModal } from './components/unlink-team-modal'
import { useTeamsData } from './hooks'

export function TeamTable({
  filteredData,
  isLoading,
  isFetching,
  filters,
  updateFilterValue,
  showFilters
}: ReturnType<typeof useTeamsData> & {
  onTeamClick?: (teamId: string) => void
  showFilters?: (keyof ReturnType<typeof useTeamsData>['filters'])[]
}) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isSuperAdmin = useIsSuperAdmin()

  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false)
  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false)

  const { organisation } = useSelectedOrganisation()
  const [unlinkTeamFromOrg] = useUnlinkTeamFromOrgMutation()

  const columns: DataTableColumnDef<RawTeamData>[] = [
    {
      id: 'logo',
      header: '',
      cell: ({ row }) => {
        const team = row.original
        return (
          <div className='flex items-center justify-center gap-2'>
            <StaticAssetImage
              src={team.logo}
              alt={team.name}
              preset={IMAGE_PRESETS.teamLogo}
              className='h-7'
            />
          </div>
        )
      }
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        const team = row.original
        return (
          <div className='flex items-center gap-2'>
            <Button
              variant='link'
              className='text-foreground w-fit px-0 text-left'
              onClick={() => navigate(`/teams/${row.original.id}`)}
            >
              {team.name}
            </Button>
          </div>
        )
      }
    },
    {
      id: 'sport',
      header: 'Sport',
      cell: ({ row }) => (
        <div className='w-32'>
          <Badge variant='outline' className='text-muted-foreground px-1.5'>
            {getSportName(row.original?.sport ?? undefined)}
          </Badge>
        </div>
      )
    },
    {
      id: 'gender',
      header: 'Gender',
      cell: ({ row }) => (
        <div className='w-32'>
          <Badge variant='outline' className='text-muted-foreground px-1.5'>
            {row.original.gender}
          </Badge>
        </div>
      )
    },
    {
      id: 'level',
      header: 'Level',
      cell: ({ row }) => (
        <div className='w-32'>
          <Badge variant='outline' className='text-muted-foreground px-1.5'>
            {row.original.level}
          </Badge>
        </div>
      )
    }
  ]

  const additionalActions: AdditionalAction<RawTeamData>[] = useMemo(() => {
    if (!isSuperAdmin) return []

    const handleUnlinkConfirm = async (team: RawTeamData) => {
      dispatch(
        addNotification({
          displayType: 'modal',
          title: 'Remove team from organisation?',
          message: `Team ${team.name} will be removed from ${organisation?.name}`,
          type: 'warning',
          action: {
            label: 'Unlink',
            onClick: async () => {
              try {
                await unlinkTeamFromOrg({
                  organisationId: organisation!.id,
                  teamId: team.id
                }).unwrap()
                toast.success('Team unlinked successfully')
              } catch (error) {
                toast.error('Failed to unlink team')
              }
            }
          }
        })
      )
    }

    return [
      {
        label: 'Unlink',
        onClick: (row: RawTeamData) => handleUnlinkConfirm(row),
        className: 'text-superadmin'
      }
    ]
  }, [organisation, dispatch, unlinkTeamFromOrg, isSuperAdmin])

  const toolbarActions: ToolbarAction[] = useMemo(() => {
    if (!isSuperAdmin) return []

    return [
      {
        label: 'Link Team',
        icon: <Link2 className='h-4 w-4' />,
        onClick: () => setIsLinkModalOpen(true),
        variant: 'superAdmin' as const,
        hideOnMobile: true
      },
      {
        label: 'Unlink Team',
        icon: <Unlink className='h-4 w-4' />,
        onClick: () => setIsUnlinkModalOpen(true),
        variant: 'superAdmin' as const,
        hideOnMobile: true
      }
    ]
  }, [isSuperAdmin])

  return (
    <>
      <DataTable
        name='Team'
        columns={columns}
        data={filteredData}
        dataForm={TeamForm}
        isLoading={isLoading || isFetching}
        additionalActions={additionalActions}
        toolbarActions={toolbarActions}
        filters={filters}
        updateFilterValue={updateFilterValue}
        showFilters={showFilters ?? []}
        defaultSorting={[{ id: 'name', desc: false }]}
      />

      <SuperAdminOnly>
        <LinkTeamModal
          isOpen={isLinkModalOpen}
          onOpenChange={setIsLinkModalOpen}
        />

        <UnlinkTeamModal
          isOpen={isUnlinkModalOpen}
          onOpenChange={setIsUnlinkModalOpen}
        />
      </SuperAdminOnly>
    </>
  )
}

// TODO: These enums should be based on the config json //
const formSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  gender: z.enum(['Unknown', 'Male', 'Female', 'Mixed']),
  level: z.enum(['Unknown', 'International', 'Club', 'U20']),
  sport: z.enum(SPORTS)
})

export function TeamForm({
  onSubmit,
  initialValues
}: {
  onSubmit: (values: z.infer<typeof formSchema>) => void
  initialValues?: z.infer<typeof formSchema>
}) {
  const [updateTeam, updateResult] = useUpdateTeamMutation()
  const [createTeam, createResult] = useCreateTeamMutation()
  const [linkTeamWithOrg] = useLinkTeamWithOrgMutation()
  const { organisation } = useSelectedOrganisation()

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      if (values.id) {
        toast.promise(
          updateTeam({
            id: values.id,
            name: values.name,
            gender: values.gender,
            level: values.level,
            sport: values.sport
          }).unwrap(),
          {
            loading: 'Updating...',
            success: (data: RawTeamData) => {
              return `${data.name} has been updated`
            },
            error: 'Error'
          }
        )
      } else {
        toast.promise(
          createTeam({
            name: values.name,
            gender: values.gender,
            level: values.level,
            sport: values.sport
          })
            .unwrap()
            .then((data) => {
              // After creating a team, link it to the organisation you're in
              if (data.id && organisation && organisation.id) {
                linkTeamWithOrg({
                  organisationId: organisation.id,
                  body: { teamId: data.id }
                }).unwrap()
              }
              return data
            }),
          {
            loading: 'Creating...',
            success: (data: RawTeamData) => {
              return `${data.name} has been created`
            },
            error: 'Error'
          }
        )
      }
      // Only close dialog after mutation succeeds
      onSubmit(values)
    } catch (err) {
      console.error('Update failed:', err)
    }
  }

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: requiredResolver(formSchema),
    defaultValues: {
      name: '',
      gender: 'Male',
      level: 'Club'
    },
    values: initialValues
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-8'>
        {form.getValues('id') && (
          <div className='flex gap-4'>
            <p className='text-sm'>Team ID</p>
            <Badge variant={'secondary'}>{form.getValues('id')}</Badge>
          </div>
        )}
        <FormField
          control={form.control}
          name='name'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Team Name</FormLabel>
              <FormControl>
                <Input placeholder='' {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='gender'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Gender</FormLabel>
              <FormControl>
                <div className='flex flex-wrap gap-2'>
                  {formSchema.shape.gender.options.map((option) => (
                    <button
                      key={option}
                      type='button'
                      onClick={() => field.onChange(option)}
                      className={cn(
                        'rounded-md border px-3 py-2 text-sm transition-colors',
                        'hover:bg-muted focus:ring-ring focus:ring-2 focus:outline-none',
                        field.value === option
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background text-foreground'
                      )}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='level'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Level</FormLabel>
              <FormControl>
                <div className='flex flex-wrap gap-2'>
                  {formSchema.shape.level.options.map((option) => (
                    <button
                      key={option}
                      type='button'
                      onClick={() => field.onChange(option)}
                      className={cn(
                        'rounded-md border px-3 py-2 text-sm transition-colors',
                        'hover:bg-muted focus:ring-ring focus:ring-2 focus:outline-none',
                        field.value === option
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background text-foreground'
                      )}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='sport'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Sport</FormLabel>
              <FormControl>
                <div className='flex flex-wrap gap-2'>
                  {formSchema.shape.sport.options.map((option) => (
                    <button
                      key={option}
                      type='button'
                      onClick={() => field.onChange(option)}
                      className={cn(
                        'rounded-md border px-3 py-2 text-sm transition-colors',
                        'hover:bg-muted focus:ring-ring focus:ring-2 focus:outline-none',
                        field.value === option
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background text-foreground'
                      )}
                    >
                      {handleName(getSport(option).name)}
                    </button>
                  ))}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className='flex justify-end'>
          <Button
            className=''
            type='submit'
            disabled={updateResult.isLoading || createResult.isLoading}
          >
            {form.getValues().id
              ? updateResult.isLoading
                ? 'Updating...'
                : 'Update'
              : createResult.isLoading
                ? 'Creating...'
                : 'Create'}
          </Button>
        </div>
      </form>
    </Form>
  )
}
